/**
 * Format a date for display (e.g., "Nov 14, 2024")
 */
export const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

/**
 * Format a date with full details (e.g., "November 14, 2024")
 */
export const formatDateLong = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });
};

/**
 * Format a date range (e.g., "Nov 14 - Nov 16, 2024" or just "Nov 14, 2024")
 * Handles both old 'date' field and new 'startDate/endDate' fields
 * Year only shows at the end for multi-day events
 */
export const formatDateRange = (event) => {
  const startDate = event.startDate || event.date;

  if (!startDate) return '';

  // If no end date or same as start date, just show single date with year
  if (!event.endDate) {
    return formatDate(startDate);
  }

  const start = new Date(startDate);
  const end = new Date(event.endDate);

  // If same date, just show once with year
  if (start.toDateString() === end.toDateString()) {
    return formatDate(startDate);
  }

  // Use UTC methods to get components to avoid timezone issues
  const startYear = start.getUTCFullYear();
  const startMonth = start.getUTCMonth();
  const startDay = start.getUTCDate();

  const endYear = end.getUTCFullYear();
  const endMonth = end.getUTCMonth();
  const endDay = end.getUTCDate();

  // Multi-day event: show start date without year, end date with year
  // If same month and year, show compact format: "Nov 14 - 16, 2024"
  if (startYear === endYear && startMonth === endMonth) {
    const monthName = start.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' });
    return `${monthName} ${startDay} - ${endDay}, ${endYear}`;
  }

  // Different months: "Nov 14 - Dec 16, 2024"
  if (startYear === endYear) {
    const startFormatted = start.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    });
    const endFormatted = end.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    });
    return `${startFormatted} - ${endFormatted}`;
  }

  // Different years, show full dates: "Dec 30, 2024 - Jan 2, 2025"
  return `${formatDate(startDate)} - ${formatDate(event.endDate)}`;
};

/**
 * Format date range with full details for display
 */
export const formatDateRangeLong = (event) => {
  const startDate = event.startDate || event.date;

  if (!startDate) return '';

  // If no end date, just show single date
  if (!event.endDate) {
    return formatDateLong(startDate);
  }

  const start = new Date(startDate);
  const end = new Date(event.endDate);

  // If same date, just show once
  if (start.toDateString() === end.toDateString()) {
    return formatDateLong(startDate);
  }

  // Show full range
  return `${formatDateLong(startDate)} - ${formatDateLong(event.endDate)}`;
};
